import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { publicCoverMessages } from './publicCoverMessages';
import { BrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import editorStyles from '../CoverEditor.module.scss';
import styles from './PublicCoverMaker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PublicTopBarProps {
  title: string;
  artist: string;
  onTitle: (value: string) => void;
  onArtist: (value: string) => void;
  /** The links of a visitor with no session; null for a signed-in visitor. */
  account: { logInHref: string; onLogIn: () => void } | null;
  onDownload: () => void;
  isDownloading: boolean;
}

const FIELD_MAX = 200;

/**
 * The top bar of the public tool (design cover-editor g1) and of its embed (g5). The two fields
 * are the song: there is no account. The full page shows the brand, "Log in" and "Sign up free";
 * the embed hides them: the website page has its own, and its browser frame holds the link to the
 * full page (the embed shows no second "Full screen" link).
 */
const PublicTopBarComponent: React.FC<PublicTopBarProps> = (props) => {
  const { title, artist, onTitle, onArtist, account, onDownload, isDownloading } = props;
  const intl = useIntl();
  const { dispatch, canUndo, canRedo, publicTool } = useCoverEditor();
  const isEmbed = publicTool?.isEmbed === true;
  const titleLabel = intl.formatMessage(publicCoverMessages.titleLabel);
  const artistLabel = intl.formatMessage(publicCoverMessages.artistLabel);

  return (
    <header className={`${editorStyles.top} ${styles.top}`}>
      {!isEmbed && (
        <span className={styles.brand}>
          <b>
            <BrandWord />
          </b>
          <small>{intl.formatMessage(publicCoverMessages.brand)}</small>
        </span>
      )}
      <div className={styles.fields}>
        <input
          className={styles.field}
          value={title}
          maxLength={FIELD_MAX}
          placeholder={titleLabel}
          aria-label={titleLabel}
          onChange={(event) => onTitle(event.target.value)}
        />
        <input
          className={styles.field}
          value={artist}
          maxLength={FIELD_MAX}
          placeholder={artistLabel}
          aria-label={artistLabel}
          onChange={(event) => onArtist(event.target.value)}
        />
      </div>
      <button
        type="button"
        className={editorStyles.topButton}
        aria-label={intl.formatMessage(coverEditorMessages.undo)}
        title={intl.formatMessage(coverEditorMessages.undo)}
        disabled={!canUndo}
        onClick={() => dispatch({ type: 'undo' })}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M9 14L4 9l5-5" />
          <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
        </svg>
      </button>
      <button
        type="button"
        className={editorStyles.topButton}
        aria-label={intl.formatMessage(coverEditorMessages.redo)}
        title={intl.formatMessage(coverEditorMessages.redo)}
        disabled={!canRedo}
        onClick={() => dispatch({ type: 'redo' })}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 14l5-5-5-5" />
          <path d="M20 9H10a6 6 0 0 0 0 12h3" />
        </svg>
      </button>
      {!isEmbed && <span className={styles.kept}>{intl.formatMessage(publicCoverMessages.kept)}</span>}
      <span className={editorStyles.grow} />
      {!isEmbed && account !== null && (
        <>
          <a className={styles.quietLink} href={account.logInHref} onClick={account.onLogIn}>
            {intl.formatMessage(publicCoverMessages.logIn)}
          </a>
          <SMLButton size="sm" outline omitCap onClick={publicTool?.onSignUp}>
            {intl.formatMessage(publicCoverMessages.signUp)}
          </SMLButton>
        </>
      )}
      <SMLButton size="sm" omitCap onClick={onDownload} isLoading={isDownloading} disabled={isDownloading}>
        {intl.formatMessage(coverEditorMessages.download)}
      </SMLButton>
    </header>
  );
};

export const PublicTopBar = TypedMemo(PublicTopBarComponent);
